import { openDB } from "idb";
import type { FrameLogger } from "../logger";
import { CrossmintFrameService } from "../service";
import type { FrameStorageAdapter } from "./frame-storage-adapter";
import {
	ALL_STORES,
	DB_NAME,
	DB_VERSION,
	MIGRATIONS,
	type OpenFrameIDB,
	type OpenFrameIDBSchema,
	type OpenFrameStore,
	type OpenFrameStoreKey,
	type OpenFrameStoreValue,
} from "./indexed-db-schema";

// iOS-specific workaround: Early IndexedDB access to prevent lazy initialization issues
function initializeIndexedDBEarly(): void {
	try {
		// Access IndexedDB early to prevent iOS Safari lazy initialization bugs
		if (typeof window !== "undefined" && "indexedDB" in window) {
			// Just accessing the property helps prevent iOS Safari bugs
			const _ = window.indexedDB;
		}
	} catch (error) {
		// Silently ignore early initialization errors
		console.debug("[IndexedDB] Early initialization warning:", error);
	}
}

// Initialize IndexedDB early when module loads
initializeIndexedDBEarly();

/**
 * FatalFrameError is thrown when IndexedDB operations fail after all retries are exhausted.
 * This signals that the WebView should be reloaded to recover from the fatal state.
 */
export class FatalFrameError extends Error {
	constructor(
		message: string,
		public readonly cause?: unknown,
	) {
		super(message);
		this.name = "FatalFrameError";
	}
}

const DEFAULT_RETRY_CONFIG = {
	maxRetries: 3,
	initialDelayMs: 100,
	maxDelayMs: 800,
	backOffFactor: 2,
};

export class IndexedDBAdapter
	extends CrossmintFrameService
	implements FrameStorageAdapter
{
	name = "IndexedDB service";
	log_prefix = "[IndexedDB]";
	dbPromise: Promise<OpenFrameIDB> | null = null;

	constructor(
		logger: FrameLogger,
		private readonly retryConfig = DEFAULT_RETRY_CONFIG,
	) {
		super(logger);
	}

	public async init(): Promise<void> {
		await this.openDB();
	}

	private async openDB(): Promise<OpenFrameIDB> {
		if (this.dbPromise != null) {
			this.logger.info(
				`${this.log_prefix} Using already open IndexedDB instance...`,
			);
			return this.dbPromise;
		}
		this.logger.info(`${this.log_prefix} Opening IndexedDB...`);

		this.dbPromise = openDB<OpenFrameIDBSchema>(DB_NAME, DB_VERSION, {
			upgrade: (db, oldVersion, newVersion, _transaction, _event) => {
				this.logger.info(
					`${this.log_prefix} Upgrading IndexedDB from version ${oldVersion} to ${newVersion}`,
				);
				this._runMigrations(db as OpenFrameIDB, oldVersion);
			},
			blocked: (currentVersion, blockedVersion, _event) => {
				this.logger.warn(
					`${this.log_prefix} open blocked: another tab/session holds the DB open during upgrade. ` +
						`Current version: ${currentVersion}, blocked version: ${blockedVersion}`,
				);
			},
			blocking: (currentVersion, blockedVersion, _event) => {
				this.logger.warn(
					`${this.log_prefix} open blocking: another tab/session holds the DB open during upgrade. ` +
						`Current version: ${currentVersion}, blocked version: ${blockedVersion}`,
				);
			},
			terminated: () => {
				this.logger.warn(`${this.log_prefix} database connection terminated.`);
				this.dbPromise = null;
			},
		})
			.then((db) => {
				db.onversionchange = (_event) => {
					this.logger.info(
						`${this.log_prefix} version change detected. Closing current connection.`,
					);
					try {
						db.close();
					} catch (error) {
						this.logger.warn(
							`${this.log_prefix} error closing database connection. Error: ${error}`,
						);
					}
					this.dbPromise = null;
				};
				this.logger.info(
					`${this.log_prefix} database connection opened successfully.`,
				);
				return db;
			})
			.catch((err) => {
				this.logger.error(
					`${this.log_prefix} error opening database connection. Error: ${err}`,
				);
				this.dbPromise = null;
				throw err;
			});

		return this.dbPromise;
	}

	public async getItem<
		S extends OpenFrameStore,
		K extends OpenFrameStoreKey<S>,
		V extends OpenFrameStoreValue<S>,
	>(storeName: S, key: K): Promise<V | undefined> {
		this.logger.info(
			`Retrieving item from store '${storeName}' with key '${key}'`,
		);
		return await this._withDB(
			async (db) => db.get<S>(storeName, key) as Promise<V | undefined>,
		);
	}

	public async setItem<
		S extends OpenFrameStore,
		K extends OpenFrameStoreKey<S>,
		V extends OpenFrameStoreValue<S>,
	>(storeName: S, key: K, value: V): Promise<void> {
		this.logger.info(`Storing item in store '${storeName}' with key '${key}'`);
		await this._withDB(async (db) => db.put(storeName, value, key));
	}

	public async removeItem<
		S extends OpenFrameStore,
		K extends OpenFrameStoreKey<S>,
	>(storeName: S, key: K): Promise<void> {
		this.logger.info(`Removing item from '${storeName}' with key '${key}'`);
		await this._withDB(async (db) => db.delete(storeName, key));
	}

	private _runMigrations(db: OpenFrameIDB, oldVersion: number) {
		this.logger.info(
			`${this.log_prefix} running migrations from version ${oldVersion} to ${DB_VERSION}`,
		);

		for (let v = oldVersion + 1; v <= DB_VERSION; v++) {
			const m = MIGRATIONS[v];
			if (m) m(db, oldVersion);
		}
	}

	private async _withDB<T>(fn: (db: OpenFrameIDB) => Promise<T>): Promise<T> {
		const { maxRetries, initialDelayMs, maxDelayMs, backOffFactor } =
			this.retryConfig;
		let attempt = 0;
		while (true) {
			try {
				const db = await this.openDB();
				return await fn(db);
			} catch (err: unknown) {
				const msg = (err as Error)?.message ?? String(err);
				this.logger.warn(`${this.log_prefix} IDB operation error: ${msg}`);

				const isTransient = this._isTransientErrorMsg(msg);
				if (!isTransient) {
					throw err;
				}

				if (attempt >= maxRetries) {
					this.logger.error(
						`${this.log_prefix} max retries (${maxRetries}) exceeded`,
					);
					throw new FatalFrameError(
						"IndexedDB operation failed after all retries exhausted",
						err,
					);
				}

				attempt++;
				const delay = Math.min(
					maxDelayMs,
					Math.round(initialDelayMs * backOffFactor ** (attempt - 1)),
				);
				this.logger.warn(
					`${this.log_prefix} retrying IDB op (attempt ${attempt}/${maxRetries}) in ${delay}ms`,
				);
				this.dbPromise = null; // Reset DB
				await new Promise((r) => setTimeout(r, delay));
			}
		}
	}
	private _isTransientErrorMsg(msg: string): boolean {
		return (
			msg.includes("The database connection is closing") ||
			msg.includes("Database connection closed") ||
			msg.includes("Connection to Indexed Database server lost") ||
			(msg.includes("InvalidStateError") && msg.includes("transaction"))
		);
	}

	public async safeDebugState(): Promise<Record<string, unknown>> {
		const baseInformation = {
			dbName: DB_NAME,
			expectedStores: ALL_STORES,
			dbVersion: DB_VERSION,
			logVersion: "4", // For troubleshooting purposes, making sure users aren't using a cached version. Bump on each release.
		};
		return await this.openDB()
			.then((db) => ({
				...baseInformation,
				currentDbVersion: db.version,
				connectionState: "idle",
				availableStores: Array.from(db.objectStoreNames),
			}))
			.catch(() => baseInformation);
	}
}
